import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../../context/AdminAuthContext';
import AdminLayout from './AdminLayout';
import { Sparkles, Save, Loader2, CheckCircle2, AlertCircle, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLOR_PRESETS = [
  { label: 'Navy', value: '#0f2b4a' },
  { label: 'Blue', value: '#1565c0' },
  { label: 'Orange', value: '#e8690c' },
  { label: 'Green', value: '#2e7d32' },
  { label: 'Red', value: '#c62828' },
  { label: 'Purple', value: '#6a1b9a' },
  { label: 'Dark', value: '#111827' },
];

const DEFAULT = {
  enabled: false,
  title: 'Special Offer!',
  message: 'Enrol today and get 20% off all courses. Limited time only!',
  badge: 'Limited Time',
  cta_text: 'View Courses',
  cta_link: '/courses',
  image: '',
  delay_seconds: 5,
  bg_color: '#0f2b4a',
  text_color: '#ffffff',
};

export default function AdminPopupAd() {
  const { authHeaders } = useAdminAuth();
  const [form, setForm] = useState(DEFAULT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [preview, setPreview] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings/popup`)
      .then(res => setForm({ ...DEFAULT, ...res.data }))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const set = (key, value) => setForm(f => ({ ...f, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    setResult(null);
    try {
      await axios.put(`${API}/admin/settings/popup`, form, { headers: authHeaders });
      setResult({ ok: true, msg: 'Popup ad settings saved.' });
    } catch (e) {
      setResult({ ok: false, msg: e?.response?.data?.detail || 'Save failed.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 size={24} className="animate-spin text-blue-500" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-2xl space-y-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Sparkles size={20} className="text-purple-500" /> Popup Ad
          </h1>
          <p className="text-gray-500 text-sm mt-0.5">
            A timed modal shown to public visitors. Dismissed once per session.
          </p>
        </div>

        {/* Live preview */}
        {preview && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
               onClick={() => setPreview(false)}>
            <div
              className="relative max-w-md w-full rounded-2xl shadow-2xl overflow-hidden"
              style={{ backgroundColor: form.bg_color, color: form.text_color }}
              onClick={e => e.stopPropagation()}
            >
              <button onClick={() => setPreview(false)}
                className="absolute top-3 right-3 rounded-full bg-black/20 hover:bg-black/40 p-1.5 z-10">
                <X size={16} />
              </button>
              {form.image && (
                <img src={form.image} alt="" className="w-full h-48 object-cover" />
              )}
              <div className="p-7">
                {form.badge && (
                  <span className="inline-block text-xs font-bold uppercase tracking-wider bg-white/20 px-3 py-1 rounded-full mb-3">
                    {form.badge}
                  </span>
                )}
                {form.title && <h3 className="text-2xl font-extrabold mb-2">{form.title}</h3>}
                {form.message && <p className="text-sm opacity-90 mb-6 leading-relaxed">{form.message}</p>}
                {form.cta_text && (
                  <span
                    className="inline-block font-bold px-6 py-2.5 rounded-lg text-sm cursor-default"
                    style={{ backgroundColor: form.text_color, color: form.bg_color }}
                  >
                    {form.cta_text}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-5">
          {/* Enable toggle */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-800">Enable popup</p>
              <p className="text-xs text-gray-500 mt-0.5">Show the popup to public visitors</p>
            </div>
            <button
              onClick={() => set('enabled', !form.enabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                form.enabled ? 'bg-blue-600' : 'bg-gray-300'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                form.enabled ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>

          {/* Badge */}
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1">Badge label (optional)</label>
            <input type="text" value={form.badge} onChange={e => set('badge', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g. Limited Time" />
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1">Title</label>
            <input type="text" value={form.title} onChange={e => set('title', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g. Special Offer!" />
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1">Message</label>
            <textarea rows={3} value={form.message} onChange={e => set('message', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              placeholder="Your promotional message…" />
          </div>

          {/* CTA */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1">Button text</label>
              <input type="text" value={form.cta_text} onChange={e => set('cta_text', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g. View Courses" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1">Button link</label>
              <input type="text" value={form.cta_link} onChange={e => set('cta_link', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="/courses" />
            </div>
          </div>

          {/* Image URL */}
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1">Image URL (optional)</label>
            <input type="text" value={form.image} onChange={e => set('image', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="https://…" />
            <p className="text-xs text-gray-400 mt-1">Displayed as a banner image at the top of the popup.</p>
          </div>

          {/* Delay */}
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1">
              Delay before showing: <span className="text-blue-600">{form.delay_seconds}s</span>
            </label>
            <input type="range" min={0} max={30} step={1} value={form.delay_seconds}
              onChange={e => set('delay_seconds', Number(e.target.value))}
              className="w-full accent-blue-600" />
            <div className="flex justify-between text-xs text-gray-400 mt-1">
              <span>0s (immediately)</span><span>30s</span>
            </div>
          </div>

          {/* Background colour */}
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-2">Background colour</label>
            <div className="flex flex-wrap gap-2">
              {COLOR_PRESETS.map(c => (
                <button key={c.value} title={c.label} onClick={() => set('bg_color', c.value)}
                  className={`w-8 h-8 rounded-full border-2 transition-transform hover:scale-110 ${
                    form.bg_color === c.value ? 'border-gray-700 scale-110' : 'border-transparent'
                  }`}
                  style={{ backgroundColor: c.value }} />
              ))}
              <div className="flex items-center gap-1.5">
                <label className="text-xs text-gray-500">Custom:</label>
                <input type="color" value={form.bg_color} onChange={e => set('bg_color', e.target.value)}
                  className="h-8 w-10 rounded cursor-pointer border border-gray-200" />
              </div>
            </div>
          </div>

          {/* Text colour */}
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-2">Text colour</label>
            <div className="flex items-center gap-3">
              {['#ffffff', '#000000', '#0f2b4a'].map(c => (
                <button key={c} onClick={() => set('text_color', c)}
                  className={`w-8 h-8 rounded-full border-2 transition-transform hover:scale-110 ${
                    form.text_color === c ? 'border-gray-700 scale-110' : 'border-gray-300'
                  }`}
                  style={{ backgroundColor: c }} />
              ))}
              <input type="color" value={form.text_color} onChange={e => set('text_color', e.target.value)}
                className="h-8 w-10 rounded cursor-pointer border border-gray-200" />
            </div>
          </div>
        </div>

        {result && (
          <div className={`flex items-center gap-2 text-sm px-4 py-3 rounded-lg ${
            result.ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
          }`}>
            {result.ok ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
            {result.msg}
          </div>
        )}

        <div className="flex gap-3">
          <button onClick={() => setPreview(true)}
            className="flex items-center gap-2 border border-gray-300 text-gray-700 hover:bg-gray-50 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors">
            Preview Popup
          </button>
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            {saving ? 'Saving…' : 'Save Popup Settings'}
          </button>
        </div>
      </div>
    </AdminLayout>
  );
}
